import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, useDeviceType } from '@cian/ui-kit';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { TThunkDispatch } from '../../types/redux';
import { CardListItem, ICard } from '../../components/CardListItem';
import { selectMainArticles, selectMainArticlesStatus } from '../../selectors/mainArticles';
import { getMainArticles, getMainArticlesSucceed } from '../../actions/mainArticles';
import { Banner } from '../Banner';
import { PR_RANDOM } from '../../components/adfox/constants';
import { SubscribeFormContainer } from '../SubscribeForm';
import { ERequestStatus } from '../../types/requestStatus';
import { selectInitialPageNumber } from '../../selectors/settings/selectInitialPageNumber';
import { setInitialPageNumber } from '../../actions/settings';
import * as s from './CardListContainer.css';

/**
 * Выводит список статей вместе с баннером, формой подписки и пагинацией
 * Слушает QS: with-tag и page
 */
export const CardListContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const { search } = useLocation();
  // eslint рекомендует юзать useMemo внутри хуков
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [withTag, setWithTag] = useState(searchParams.get('with-tag') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') as string, 10) || 1);
  const history = useHistory();
  const deviceType = useDeviceType();
  const isPhone = () => deviceType === 'phone';
  const articles = useSelector(selectMainArticles);
  const articlesStatus = useSelector(selectMainArticlesStatus);
  const initialPageNumber = useSelector(selectInitialPageNumber);

  useEffect(() => {
    const qsPage = parseInt(searchParams.get('page') as string, 10) || 1;

    /**
     * Если приложение инициализировано не на первой странице, то,
     * переходя на первую, устанавливаем InitialPageNumber = 1,
     * т.к. первая страница отличается визуально
     */
    if (initialPageNumber !== 1 && qsPage === 1) {
      dispatch(setInitialPageNumber(qsPage));
    }

    /**
     * При обратной пагинации обнуляем стор
     */
    if (qsPage < currentPage) {
      dispatch(getMainArticlesSucceed([]));
    }
  }, [currentPage, dispatch, initialPageNumber, searchParams]);

  /**
   * Обновляет/дополняет список статей
   * Обновляет состояние текущей страницы
   * Обновляет состояние текущего тэга
   */
  useEffect(() => {
    const qsPage = parseInt(searchParams.get('page') as string, 10) || 1;
    const qsWithTag = searchParams.get('with-tag') || '';

    /**
     * Если страница не изменилась,
     * и тэг не изменился,
     * и статьи предварительно загружены -> ничего не делаем
     */
    if (qsPage === currentPage && qsWithTag === withTag && articlesStatus !== ERequestStatus.Initial) {
      return;
    }

    dispatch(
      getMainArticles({ tag: qsWithTag, page: qsPage, setType: qsPage > currentPage ? 'append' : 'set' }),
    ).finally();
    setCurrentPage(qsPage);
    setWithTag(qsWithTag);
  }, [articlesStatus, currentPage, dispatch, searchParams, withTag]);

  /** При клике по шильдику рубрики в плашке статьи роутит на соответствующую рубрику */
  const handleRubricButtonClick: ICard['onRubricButtonClick'] = useCallback(
    (e, url) => {
      e.stopPropagation();
      history.push(url);
    },
    [history],
  );

  /** При клике по плашке статьи роутит на соответствующую страницу статьи */
  const handleArticleClick = useCallback(
    (url: string) => {
      history.push(url);
    },
    [history],
  );

  /** Увеличивает номер текущей страницы на 1 и роутит */
  const handleReadMore = useCallback(() => {
    searchParams.set('page', (currentPage + 1).toString());
    history.push(`/magazine/?${searchParams.toString()}`);
  }, [currentPage, history, searchParams]);

  /** Параметры баннера в листинге для Адфокса */
  const params = {
    p1: 'cxytw',
    p2: 'gqui',
    puid15: 'content_part',
    pr: PR_RANDOM,
    pct: 'a',
    pfc: 'a',
    pfb: 'a',
    plp: 'a',
    pli: 'a',
    pop: 'a',
    pt: 'b',
  };

  if (!articles?.length) {
    return null;
  }

  return (
    <div className={`${s['wrapper']}`}>
      <div className={`${s['list-wrapper']}`}>
        {articles.map(({ id, attributes: { image, title, subtitle, datePublish, rubrics, slug } }, index) => (
          <Fragment key={`card-list-item-${index}`}>
            {/* Баннер и форма подписки, если приложение инициализировано на первой странице */}
            {initialPageNumber === 1 && index === 5 && (
              <>
                <Col xs={12} m={6} l={4}>
                  <Banner place={'mainpage'} constants={params} height={'100%'} className={'wrapper-100'} />
                </Col>

                <div className={`${s['subscribe-form-wrapper']}`}>
                  <SubscribeFormContainer />
                </div>
              </>
            )}

            {/* Статья */}
            <Col xs={12} m={6} l={4}>
              <CardListItem
                title={title}
                subtitle={image ? undefined : subtitle}
                height={isPhone() ? 316 : 400}
                image={image}
                rubric={rubrics?.length ? rubrics[0] : undefined}
                datePublish={datePublish}
                onRubricButtonClick={handleRubricButtonClick}
                onClick={() => handleArticleClick(`/stati-${slug}-${id}/`)}
              />
            </Col>

            {/* Форма подписки, если приложение инициализировано НЕ на первой странице */}
            {initialPageNumber !== 1 && index === 5 && (
              <div className={`${s['subscribe-form-wrapper']}`}>
                <SubscribeFormContainer />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div className={`${s['button-wrapper']}`}>
        <Button theme={'fill_secondary'} loading={articlesStatus === 'loading'} onClick={handleReadMore}>
          Больше
        </Button>
      </div>
    </div>
  );
};
